import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminInternshipManagement = ({ onNavigate, userInfo = {} }) => {
  // Mobile state (used for responsive content layout)
  const [isMobile, setIsMobile] = useState(false);

  // State for internships data
  const [internships, setInternships] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit] = useState(10);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInternship, setSelectedInternship] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    student_id: '',
    company_name: '',
    position: '',
    supervisor_name: '',
    supervisor_contact: '',
    start_date: '',
    end_date: '',
    status: 'active',
    notes: '',
    company_latitude: '',
    company_longitude: '',
    allowed_radius_meters: 100,
    location_verification_enabled: true
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Check if mobile on mount and resize (used for responsive content layout)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch internships from API
  const fetchInternships = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && search.trim() && { search: search.trim() })
      });

      const apiUrl = `${API_LINK}/api/admin/internships?${params}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          return;
        } else if (response.status === 404) {
          setError('Admin API endpoint not found. Please check if the backend server is running correctly.');
          return;
        } else if (response.status === 500) {
          setError('Server error occurred. Please check the backend logs.');
          return;
        }

        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setError('Server returned invalid response format. Expected JSON.');
        return;
      }

      const result = await response.json();

      if (result.success) {
        const internshipsData = result.data?.internships || [];
        const paginationData = result.data?.pagination || {};

        setInternships(internshipsData);
        setCurrentPage(paginationData.currentPage || page);
        setTotalPages(paginationData.totalPages || 1);
        setTotalCount(paginationData.totalCount || internshipsData.length);
      } else {
        setError(result.message || 'Failed to fetch internships from server');
      }
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running \n2. Admin API routes are properly configured\n3. Database connection is working');
      } else if (error.message.includes('JSON')) {
        setError('Server returned invalid data format. Please check backend API.');
      } else {
        setError(`Failed to load internships: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch students for dropdown
  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/admin/students?limit=1000`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStudents(result.data?.students || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInternships(1, '');
      fetchStudents();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    fetchInternships(1, searchTerm);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    fetchInternships(1, '');
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page && page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchInternships(page, searchTerm);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.student_id.trim()) errors.student_id = 'Student is required';
    if (!formData.company_name.trim()) errors.company_name = 'Company name is required';
    if (!formData.position.trim()) errors.position = 'Position is required';
    if (!formData.supervisor_name.trim()) errors.supervisor_name = 'Supervisor name is required';
    if (!formData.start_date) errors.start_date = 'Start date is required';
    if (!formData.end_date) errors.end_date = 'End date is required';

    // Date validation
    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate <= startDate) {
        errors.end_date = 'End date must be after start date';
      }
    }

    // Email validation for supervisor contact if provided
    if (formData.supervisor_contact && formData.supervisor_contact.includes('@')) {
      if (!/\S+@\S+\.\S+/.test(formData.supervisor_contact)) {
        errors.supervisor_contact = 'Invalid email format';
      }
    }

    // Location validation
    if (formData.company_latitude && (formData.company_latitude < -90 || formData.company_latitude > 90)) {
      errors.company_latitude = 'Latitude must be between -90 and 90';
    }

    if (formData.company_longitude && (formData.company_longitude < -180 || formData.company_longitude > 180)) {
      errors.company_longitude = 'Longitude must be between -180 and 180';
    }

    if (formData.allowed_radius_meters && (formData.allowed_radius_meters < 10 || formData.allowed_radius_meters > 10000)) {
      errors.allowed_radius_meters = 'Allowed radius must be between 10 and 10000 meters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Handle form submission for add/edit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = selectedInternship
        ? `${API_LINK}/api/admin/internships/${selectedInternship.id}`
        : `${API_LINK}/api/admin/internships`;

      const method = selectedInternship ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();
        fetchInternships(currentPage || 1, searchTerm);
        alert(selectedInternship ? 'Internship updated successfully!' : 'Internship added successfully!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert('Failed to save internship. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedInternship) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/admin/internships/${selectedInternship.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setSelectedInternship(null);
        fetchInternships(currentPage || 1, searchTerm);
        alert('Internship deleted successfully!');
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      alert('Failed to delete internship. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      student_id: '',
      company_name: '',
      position: '',
      supervisor_name: '',
      supervisor_contact: '',
      start_date: '',
      end_date: '',
      status: 'active',
      notes: '',
      company_latitude: '',
      company_longitude: '',
      allowed_radius_meters: 100,
      location_verification_enabled: true
    });
    setFormErrors({});
    setSelectedInternship(null);
  };

  // Handle edit click
  const handleEditClick = (internship) => {
    setSelectedInternship(internship);
    setFormData({
      student_id: internship.student_id || '',
      company_name: internship.company_name || '',
      position: internship.position || '',
      supervisor_name: internship.supervisor_name || '',
      supervisor_contact: internship.supervisor_contact || '',
      start_date: internship.start_date ? internship.start_date.split('T')[0] : '',
      end_date: internship.end_date ? internship.end_date.split('T')[0] : '',
      status: internship.status || 'active',
      notes: internship.notes || '',
      company_latitude: internship.company_latitude || '',
      company_longitude: internship.company_longitude || '',
      allowed_radius_meters: internship.allowed_radius_meters || 100,
      location_verification_enabled: internship.location_verification_enabled !== undefined ?
        internship.location_verification_enabled : true
    });
    setShowEditModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (internship) => {
    setSelectedInternship(internship);
    setShowDeleteModal(true);
  };



  // Get student name by ID
  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    return student ? student.name : studentId;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'completed':
        return { bg: '#e3f2fd', color: '#1976d2' };
      case 'pending':
        return { bg: '#fff3e0', color: '#e65100' };
      default:
        return { bg: '#f5f5f5', color: '#666' };
    }
  };

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-internships" userInfo={userInfo}>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          Internship Management Portal 🎓
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Manage all internship records, assign internships to students, and monitor internship progress.
        </p>
      </div>

      {/* Search and Controls Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Search Bar */}
          <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '400px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by company, position, or student..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '1rem 3rem 1rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#238548';
                  e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />

              <button
                onClick={handleSearch}
                style={{
                  position: 'absolute',
                  right: searchTerm ? '40px' : '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f1f5f9';
                  e.target.style.color = '#238548';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#666';
                }}
                title="Search internships"
              >
                🔍
              </button>

              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fee2e2';
                    e.target.style.color = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Add Internship Button */}
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>+</span>
            Add New Internship
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid #ecf0f1',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <span>
            {loading ? 'Loading internships...' :
              error ? 'Error loading internships' :
                `Showing ${internships.length} of ${totalCount} internships`}
            {searchTerm && !loading && !error && (
              <span style={{ color: '#238548', fontWeight: '500' }}>
                {` (filtered by "${searchTerm}")`}
              </span>
            )}
            {searchTerm && !loading && !error && internships.length === 0 && (
              <span style={{ color: '#dc2626', marginLeft: '0.5rem' }}>
                - No matches found
              </span>
            )}
          </span>
          {loading && (
            <span style={{ color: '#238548' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e3f2fd',
                borderTop: '2px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }}></div>
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Internships</p>
          <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
          <button
            onClick={() => fetchInternships(currentPage, searchTerm)}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Internships Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading && internships.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading internships...</p>
          </div>
        ) : internships.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>🎓</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 1rem 0'
            }}>
              {searchTerm ? 'No internships found' : 'No internships assigned yet'}
            </h3>
            <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
              {searchTerm
                ? `No internships match "${searchTerm}". Try adjusting your search criteria.`
                : 'Get started by assigning the first internship to a student'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Assign First Internship
              </button>
            )}
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginLeft: '1rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e5e7eb';
                  e.target.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.borderColor = '#d1d5db';
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.85rem' : '0.95rem'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Student</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Company</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Position</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Duration</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Supervisor</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Status</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Location</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {internships.map((internship, index) => {
                    const statusStyle = getStatusColor(internship.status);
                    return (
                      <tr key={internship.id} style={{
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background-color 0.3s ease'
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '1rem' }}>
                          <div>
                            <div style={{ fontWeight: 500, color: '#333', marginBottom: '0.25rem' }}>
                              {getStudentName(internship.student_id)}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                              ID: {internship.student_id}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem', color: '#333', fontWeight: 500 }}>
                          {internship.company_name}
                        </td>
                        <td style={{ padding: '1rem', color: '#333' }}>
                          {internship.position}
                        </td>
                        <td style={{ padding: '1rem', color: '#333' }}>
                          <div style={{ fontSize: '0.9rem' }}>
                            <div>{formatDate(internship.start_date)} -</div>
                            <div>{formatDate(internship.end_date)}</div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem', color: '#333' }}>
                          <div>
                            <div style={{ fontWeight: 500 }}>{internship.supervisor_name}</div>
                            {internship.supervisor_contact && (
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                {internship.supervisor_contact}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            textTransform: 'capitalize'
                          }}>
                            {internship.status}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div>
                            {internship.company_latitude && internship.company_longitude ? (
                              <div style={{ fontSize: '0.85rem' }}>
                                <div style={{ color: '#28a745', fontWeight: 500 }}>
                                  📍 Location Set
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                  Radius: {internship.allowed_radius_meters || 100}m
                                </div>
                                {internship.location_verification_enabled && (
                                  <div style={{ fontSize: '0.75rem', color: '#007bff' }}>
                                    ✓ Verification Enabled
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div style={{ fontSize: '0.85rem', color: '#dc3545' }}>
                                📍 No Location
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleEditClick(internship)}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#1976d2';
                                e.target.style.color = '#ffffff';
                                e.target.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#e3f2fd';
                                e.target.style.color = '#1976d2';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteClick(internship)}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#ffebee',
                                color: '#d32f2f',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#d32f2f';
                                e.target.style.color = '#ffffff';
                                e.target.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ffebee';
                                e.target.style.color = '#d32f2f';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages} • {totalCount} total internships
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let page;
                    if (totalPages <= 5) {
                      page = index + 1;
                    } else if (currentPage <= 3) {
                      page = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + index;
                    } else {
                      page = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '2px solid',
                          borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                          backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                          color: page === currentPage ? '#ffffff' : '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '0.9rem',
                          fontWeight: page === currentPage ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#238548';
                            e.target.style.backgroundColor = '#f0fdf4';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              padding: '1.5rem 2rem',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {selectedInternship ? '✏️ Edit Internship' : '➕ Assign New Internship'}
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                {selectedInternship ? 'Update internship information' : 'Assign internship to a student'}
              </p>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '2rem',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                {/* Student Selection */}
                {/* Student ID Input */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Student ID *
                  </label>
                  <input
                    type="text"
                    value={formData.student_id}
                    onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                    placeholder="Enter Student ID (e.g., STU2024001)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.student_id ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.student_id ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.student_id) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.student_id) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.student_id && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.student_id}
                    </p>
                  )}
                  <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                    Enter the exact Student ID as registered in the system
                  </p>
                </div>
                {/* Company Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="e.g., TechCorp Inc."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.company_name ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.company_name ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.company_name) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.company_name) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.company_name && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.company_name}
                    </p>
                  )}
                </div>

                {/* Position */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Position *
                  </label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="e.g., Software Developer Intern"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.position ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.position ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.position) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.position) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.position && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.position}
                    </p>
                  )}
                </div>

                {/* Supervisor Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Supervisor Name *
                  </label>
                  <input
                    type="text"
                    value={formData.supervisor_name}
                    onChange={(e) => setFormData({ ...formData, supervisor_name: e.target.value })}
                    placeholder="Enter supervisor's name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.supervisor_name ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.supervisor_name ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.supervisor_name) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.supervisor_name) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.supervisor_name && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.supervisor_name}
                    </p>
                  )}
                </div>

                {/* Supervisor Contact */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Supervisor Contact
                  </label>
                  <input
                    type="text"
                    value={formData.supervisor_contact}
                    onChange={(e) => setFormData({ ...formData, supervisor_contact: e.target.value })}
                    placeholder="Email or phone number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.supervisor_contact ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.supervisor_contact ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.supervisor_contact) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.supervisor_contact) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.supervisor_contact && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.supervisor_contact}
                    </p>
                  )}
                </div>

                {/* Start Date */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.start_date ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.start_date ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.start_date) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.start_date) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.start_date && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.start_date}
                    </p>
                  )}
                </div>

                {/* End Date */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.end_date ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.end_date ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.end_date) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.end_date) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.end_date && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.end_date}
                    </p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                {/* Notes */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes about the internship..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
              {/* Company Location Section */}
              <div style={{ gridColumn: '1 / -1', marginTop: '1rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '1rem', fontWeight: 600 }}>
                  📍 Company Location & Verification
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  {/* Company Latitude */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Company Latitude
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      min="-90"
                      max="90"
                      value={formData.company_latitude}
                      onChange={(e) => setFormData({ ...formData, company_latitude: e.target.value })}
                      placeholder="e.g., 22.5726"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.company_latitude ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.company_latitude ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.company_latitude) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.company_latitude) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.company_latitude && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.company_latitude}
                      </p>
                    )}
                  </div>

                  {/* Company Longitude */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Company Longitude
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      min="-180"
                      max="180"
                      value={formData.company_longitude}
                      onChange={(e) => setFormData({ ...formData, company_longitude: e.target.value })}
                      placeholder="e.g., 88.3639"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.company_longitude ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.company_longitude ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.company_longitude) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.company_longitude) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.company_longitude && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.company_longitude}
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'end' }}>
                  {/* Allowed Radius */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Allowed Radius (meters)
                    </label>
                    <input
                      type="number"
                      min="10"
                      max="10000"
                      value={formData.allowed_radius_meters}
                      onChange={(e) => setFormData({ ...formData, allowed_radius_meters: e.target.value })}
                      placeholder="100"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.allowed_radius_meters ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.allowed_radius_meters ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.allowed_radius_meters) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.allowed_radius_meters) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.allowed_radius_meters && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.allowed_radius_meters}
                      </p>
                    )}
                  </div>

                  {/* Location Verification Toggle */}
                  <div>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      transition: 'border-color 0.3s ease'
                    }}
                      onMouseEnter={(e) => e.target.style.borderColor = '#238548'}
                      onMouseLeave={(e) => e.target.style.borderColor = '#e5e7eb'}
                    >
                      <input
                        type="checkbox"
                        checked={formData.location_verification_enabled}
                        onChange={(e) => setFormData({ ...formData, location_verification_enabled: e.target.checked })}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#238548'
                        }}
                      />
                      Enable Location Verification
                    </label>
                  </div>
                </div>

                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                  💡 Tip: Use Google Maps to get accurate latitude and longitude coordinates for the company location.
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              backgroundColor: '#f8fafc'
            }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Saving...
                  </>
                ) : (
                  <>
                    {selectedInternship ? '💾 Update Internship' : '🎓 Assign Internship'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedInternship && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
            width: '100%'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '2rem 2rem 1rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                🗑️
              </div>
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#dc2626'
              }}>
                Delete Internship
              </h3>
              <p style={{
                margin: '0 0 1rem 0',
                color: '#6b7280',
                lineHeight: 1.6
              }}>
                Are you sure you want to delete the internship at <strong style={{ color: '#374151' }}>{selectedInternship.company_name}</strong> for student <strong style={{ color: '#374151' }}>{getStudentName(selectedInternship.student_id)}</strong>?
              </p>
              <p style={{
                margin: 0,
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                This action cannot be undone and will permanently remove all internship data.
              </p>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 2rem 2rem 2rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedInternship(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Deleting...
                  </>
                ) : (
                  '🗑️ Delete Internship'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminInternshipManagement;